if (!customElements.get("collection-tabs")) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        images: [".f-collection-tab__image"],
        collapsibleTabs: ["collapsible-tab"],
      };
      this.handleMouseEnter = this.onMouseEnterCollapsibleTabs.bind(this);
      this.handleScroll = this.onScroll.bind(this);
    }

    init() {
      this.domNodes = queryDomNodes(this.selectors, this);
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplayDuration = parseInt(this.dataset.autoplayDuration, 10) || 5;
      this.autoplayTimer = null;
      this.tabActiveIndex = 0;
      this.totalTabs = this.domNodes.collapsibleTabs.length;
      this.isInView = false;
      this.isScrolling = false;
      this.scrollTimer = null;
      this.hoverToOpen =
        this.dataset.triggerBehavior === "hover" &&
        window.matchMedia("(hover: hover)").matches;
    }

    connectedCallback() {
      this.init();

      window.FoxThemeEvents.subscribe(
        "ON_COLLAPSIBLE_TAB_SELECTED",
        (collapsibleTab) => {
          if (collapsibleTab.classList.contains("collection-tab")) {
            const collectionTabs = collapsibleTab.closest("collection-tabs");
            collectionTabs?.onElementSelected(collapsibleTab);
          }
        }
      );

      if (this.autoplay) {
        this.style.setProperty(
          "--autoplay-duration",
          `${this.autoplayDuration}s`
        );

        this.observer = new IntersectionObserver(
          ([entry]) => {
            entry.isIntersecting ? this.enterView() : this.leaveView();
          },
          { rootMargin: "0px", threshold: 0.25 }
        );
        this.observer.observe(this);
      }

      if (this.hoverToOpen) {
        this.domNodes.collapsibleTabs.forEach((tab) => {
          tab.addEventListener("mouseenter", this.handleMouseEnter);
        });
        window.addEventListener("scroll", this.handleScroll, { passive: true });
      }
    }

    // Starts autoplay only when section is visible; keeps current tab still.
    enterView() {
      if (this.isInView) return;

      this.isInView = true;
      this.classList.add("is-autoplaying");
      this.preloadImages();
      this.scheduleNext();
    }

    leaveView() {
      if (!this.isInView) return;

      this.isInView = false;
      this.classList.remove("is-autoplaying");
      clearTimeout(this.autoplayTimer);
    }

    scheduleNext() {
      if (!this.autoplay || !this.isInView) return;

      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = setTimeout(
        () => this.openNextTab(),
        this.autoplayDuration * 1000
      );
    }

    openNextTab() {
      if (!this.isInView) return;

      const nextIndex =
        this.tabActiveIndex + 1 >= this.totalTabs ? 0 : this.tabActiveIndex + 1;
      const nextTab = this.domNodes.collapsibleTabs[nextIndex];
      if (!nextTab || nextTab.selected) return;

      this.tabActiveIndex = nextIndex;
      nextTab.open?.() ?? nextTab.toggle();
    }

    onElementSelected(collapsibleTab) {
      const index = this.domNodes.collapsibleTabs.indexOf(collapsibleTab);
      if (index >= 0) this.tabActiveIndex = index;
      this.syncImages(collapsibleTab);
      if (this.autoplay && this.isInView) this.scheduleNext();
    }

    syncImages(collapsibleTab) {
      this.domNodes.images?.forEach((image) => {
        const isActive =
          collapsibleTab.dataset.blockId === image.dataset.blockId;
        image.classList.toggle("is-active", isActive);
        if (isActive) this.preloadImage(image);
      });
    }

    preloadImages() {
      this.domNodes.images?.forEach((image) => this.preloadImage(image));
    }

    preloadImage(imageEl) {
      if (!imageEl) return;

      const responsiveImage = imageEl.querySelector("responsive-image");
      if (responsiveImage) {
        if (typeof responsiveImage.loadImage === "function") {
          responsiveImage.loadImage();
        }

        const img = responsiveImage.querySelector("img");
        if (img?.loading === "lazy") img.loading = "eager";
        return;
      }

      const img = imageEl.querySelector("img");
      if (img?.loading === "lazy") img.loading = "eager";
    }

    onScroll() {
      this.isScrolling = true;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.isScrolling = false;
      }, 200);
    }

    onMouseEnterCollapsibleTabs(event) {
      if (this.isScrolling) return;
      event.currentTarget.toggle(event);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearTimeout(this.autoplayTimer);
      clearTimeout(this.scrollTimer);
      window.removeEventListener("scroll", this.handleScroll);

      if (this.hoverToOpen) {
        this.domNodes.collapsibleTabs.forEach((tab) => {
          tab.removeEventListener("mouseenter", this.handleMouseEnter);
        });
      }
    }
  }
  customElements.define("collection-tabs", CollectionTabs);
}
